import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogContent, IconButton,
  LinearProgress, Stack, Typography,
} from '@mui/material';
import { AutoAwesomeOutlined, Close, RedeemOutlined } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { spinRoulette } from '../../services/subscribe';

const money = (value) => `${Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
const formatDate = (value) => value ? new Date(value).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const FALLBACK_STAKES = [
  { stake: 10, title: 'Мягкий круг', top_reward: 'до 15 ₽ / +10%' },
  { stake: 25, title: 'Базовый бонус', top_reward: 'до 50 ₽ / +15%' },
  { stake: 50, title: 'Усиленный', top_reward: 'до 100 ₽ / +25%' },
  { stake: 100, title: 'Максимальный', top_reward: 'до 250 ₽ / +35%' },
];

const FRIENDLY_STAKE_TITLES = {
  10: 'Мягкий круг',
  25: 'Базовый бонус',
  50: 'Усиленный',
  100: 'Максимальный',
};

const WHEEL_SEGMENTS = {
  10: ['Пауза', '+3 ₽', '+7 ₽', '+10%', 'Бонус', '+15 ₽', '+3 ₽', '+10%'],
  25: ['Пауза', '+10 ₽', '+20 ₽', '+15%', 'Бонус', '+50 ₽', '+10 ₽', '+15%'],
  50: ['Пауза', '+20 ₽', '+45 ₽', '+25%', 'Бонус', '+100 ₽', '+20 ₽', '+25%'],
  100: ['Пауза', '+40 ₽', '+90 ₽', '+35%', 'Бонус', '+250 ₽', '+40 ₽', '+35%'],
};

const wheelSegments = (stake) => WHEEL_SEGMENTS[Number(stake)] || WHEEL_SEGMENTS[10];

const COLORS = ['#14243d', '#1688ff', '#20b8ff', '#7847ff', '#1a2c49', '#9b45ff', '#0b9ce8', '#3458ff'];

const polar = (cx, cy, radius, angle) => {
  const radians = (angle - 90) * Math.PI / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
};

const sectorPath = (startAngle, endAngle, radius = 150) => {
  const start = polar(160, 160, radius, endAngle);
  const end = polar(160, 160, radius, startAngle);
  return [
    `M 160 160`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 0 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
};

const rewardSegmentIndex = (reward, stake) => {
  const segments = wheelSegments(stake);
  if (!reward || reward.type === 'nothing') return 0;
  if (reward.type === 'deposit_bonus') {
    const label = `+${reward.percent}%`;
    const index = segments.indexOf(label);
    return index >= 0 ? index : 3;
  }
  const label = `+${Number(reward.amount || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
  const index = segments.indexOf(label);
  return index >= 0 ? index : 1;
};

const friendlyRewardLabel = (reward) => {
  if (!reward) return '';
  if (reward.type === 'nothing') return 'Пауза — бонус в следующий раз';
  return reward.label;
};

const Wheel = ({ rotation, selectedStake }) => {
  const segments = wheelSegments(selectedStake);
  return (
    <Box className="roulette-stage">
      <div className="roulette-glow" />
      <div className="roulette-pointer" />
      <svg className="roulette-svg" viewBox="0 0 320 320" style={{ transform: `rotate(${rotation}deg)` }}>
        <defs>
          <filter id="sliceShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#00142d" floodOpacity=".35" />
          </filter>
        </defs>
        <circle cx="160" cy="160" r="154" fill="#07182c" />
        {segments.map((label, index) => {
          const start = index * 45 - 22.5;
          const end = start + 45;
          const mid = start + 22.5;
          const textPoint = polar(160, 160, 103, mid);
          return (
            <g key={`${label}-${index}`}>
              <path d={sectorPath(start, end)} fill={COLORS[index % COLORS.length]} stroke="rgba(255,255,255,.16)" strokeWidth="2" filter="url(#sliceShadow)" />
              <g transform={`translate(${textPoint.x} ${textPoint.y}) rotate(${mid})`}>
                <rect className="roulette-label-bg" x="-28" y="-13" width="56" height="26" rx="13" />
                <text className="roulette-svg-label" textAnchor="middle" dominantBaseline="middle">{label}</text>
              </g>
            </g>
          );
        })}
        <circle cx="160" cy="160" r="58" fill="#bde9ff" stroke="rgba(255,255,255,.72)" strokeWidth="6" />
        <circle cx="160" cy="160" r="42" fill="#82d0ff" />
        <g className="roulette-svg-center">
          <text x="160" y="154" textAnchor="middle">✦</text>
          <text x="160" y="178" textAnchor="middle">{money(selectedStake)}</text>
        </g>
      </svg>
    </Box>
  );
};

const RewardView = ({ reward }) => {
  if (!reward) return null;
  const isBonus = reward.type === 'deposit_bonus';
  return (
    <Box className="reward-pop">
      <Typography variant="overline" color="primary.light">Итог круга</Typography>
      <Typography variant="h5">{friendlyRewardLabel(reward)}</Typography>
      {isBonus && (
        <Typography color="text.secondary" mt={1}>
          Действует до {formatDate(reward.expires_at)} и применится к следующему пополнению.
        </Typography>
      )}
    </Box>
  );
};

const WeeklyRoulette = ({ roulette, balance, onWon }) => {
  const stakes = useMemo(() => {
    const items = roulette?.stakes?.length ? roulette.stakes : FALLBACK_STAKES;
    return items.map((item) => {
      const stake = Number(item.stake);
      return { ...item, stake, title: FRIENDLY_STAKE_TITLES[stake] || item.title };
    });
  }, [roulette?.stakes]);
  const [selectedStake, setSelectedStake] = useState(stakes[0]?.stake || 10);
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [reward, setReward] = useState(null);
  const canAfford = Number(balance || 0) >= selectedStake;
  const activeBonus = roulette?.active_deposit_bonus;

  const spin = async () => {
    setSpinning(true);
    setReward(null);
    try {
      const result = await spinRoulette(selectedStake);
      const details = result.reward_details || { type: Number(result.reward) > 0 ? 'balance' : 'nothing', amount: result.reward, label: `${result.reward || 0} ₽` };
      const segmentIndex = rewardSegmentIndex(details, selectedStake);
      const segmentSize = 45;
      setRotation((current) => {
        const normalized = ((current % 360) + 360) % 360;
        const targetAngle = (360 - segmentIndex * segmentSize) % 360;
        const delta = (targetAngle - normalized + 360) % 360;
        return current + 1800 + delta;
      });
      window.setTimeout(async () => {
        setReward(details);
        setSpinning(false);
        await onWon?.();
        toast.success(friendlyRewardLabel(result.reward_details) || 'Круг завершён');
      }, 4200);
    } catch (error) {
      setSpinning(false);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Не удалось запустить бонусный круг');
    }
  };

  return (
    <>
      <Card className="roulette-card">
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box className="round-icon"><AutoAwesomeOutlined /></Box>
              <Box>
                <Typography variant="h5">Бонусный круг</Typography>
                <Typography color="text.secondary" variant="body2">Выберите вклад — получите бонус к балансу или пополнению</Typography>
              </Box>
            </Stack>
            <Chip color="primary" label="ДОСТУПНО" size="small" />
          </Stack>

          {activeBonus && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Активен бонус +{activeBonus.percent}% к пополнению до {formatDate(activeBonus.expires_at)}
            </Alert>
          )}

          <Box className="stake-grid" mb={2.5}>
            {stakes.map((item) => {
              const selected = selectedStake === item.stake;
              const disabled = Number(balance || 0) < item.stake;
              return (
                <button
                  key={item.stake}
                  type="button"
                  className={`stake-card ${selected ? 'selected' : ''}`}
                  disabled={spinning}
                  onClick={() => setSelectedStake(item.stake)}
                >
                  <span>{item.title}</span>
                  <b>{money(item.stake)}</b>
                  <small>{item.top_reward}</small>
                  {disabled && <em>не хватает баланса</em>}
                </button>
              );
            })}
          </Box>

          <Button
            fullWidth
            variant="contained"
            disabled={!canAfford}
            onClick={() => { setReward(null); setOpen(true); }}
            startIcon={<RedeemOutlined />}
          >
            Открыть круг за {money(selectedStake)}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={spinning ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent className="roulette-dialog">
          <IconButton disabled={spinning} onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 10, top: 10 }}>
            <Close />
          </IconButton>
          <Typography color="primary.light" fontWeight={800} letterSpacing=".14em">NOX BONUS</Typography>
          <Typography variant="h4" mb={1}>Бонусный круг</Typography>
          <Typography color="text.secondary" className="roulette-subtitle" mb={1.5} textAlign="center" mx="auto">
            Вклад {money(selectedStake)} · рубли на баланс или временный бонус к пополнению
          </Typography>

          <Wheel rotation={rotation} selectedStake={selectedStake} />
          {spinning && <LinearProgress sx={{ mt: 3, borderRadius: 2 }} />}
          <RewardView reward={reward} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" mt={4}>
            <Button variant="outlined" disabled={spinning} onClick={() => setOpen(false)}>Закрыть</Button>
            <Button variant="contained" size="large" disabled={spinning || !canAfford} onClick={spin}>
              {spinning ? 'Круг выбирает бонус…' : `Получить бонус за ${money(selectedStake)}`}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WeeklyRoulette;
